import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { jwtDecode } from "jwt-decode";
import { onClientLoginSuccess } from '@/lib/utils';
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface ResetPasswordDialogProps {
  isOpen: boolean;
  mode: string;
  onClose: () => void;
}

const ResetPasswordDialog = ({ isOpen, mode, onClose }: ResetPasswordDialogProps) => {
  const { t } = useTranslation(); // Access the translation function
  const { setUser } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      toast({
        title: t("resetPassword.toastPasswordMismatchTitle"),
        description: t("resetPassword.toastPasswordMismatchDescription"),
        variant: "destructive",
        duration: 5000,
      });
      return; // Stop form submission
    }

    setIsSubmitting(true);

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token') || '';

      const response = await fetch(`${import.meta.env.VITE_CLIENT_RESET_PASSWORD_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, newPassword, confirmPassword }),
      });

      if (!response.ok) {
        throw new Error("Invalid email or password");
      }

      const data = await response.json();

      setUser(onClientLoginSuccess(data.token));

      // Remove the query string from the URL
      window.history.replaceState({}, document.title, window.location.pathname);

      onClose();

      toast({
        title: t("resetPassword.toastSuccessTitle"),
        description: t("resetPassword.toastSuccessDescription"),
        className: "bg-green-500 text-white border-green-600", // Toast style for success
        duration: 5000,
      });

    } catch (error) {
      console.error("Error reset password:", error);
      toast({
        title: t("resetPassword.toastErrorTitle"),
        description: t("resetPassword.toastErrorDescription"),
        variant: "destructive", // Toast style for errors
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'confirmregistration' ? t("resetPassword.confirmRegistrationTitle") : t("resetPassword.title")}
          </DialogTitle>
          <DialogDescription>
            {mode === 'confirmregistration' ? t("resetPassword.confirmRegistrationDescription") : t("resetPassword.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("resetPassword.newPasswordLabel")}</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("resetPassword.confirmPasswordLabel")}</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col space-y-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("resetPassword.sendingButton") : t("resetPassword.sendResetLinkButton")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordDialog;
